import PropTypes from 'prop-types';
import React, { useState } from 'react';

import Dropzone from 'react-dropzone';
import ImageCropModal from './ImageCropModal';
import { fileToDataUrl } from './cropUtils';
import { toastError } from './toastHelper';

// Matches validates_attachment_size on the *_source attachments. Over this, storing
// the source would fail the whole PATCH and take the crop the user just made with
// it, so we keep the crop and store no source.
const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

const DetailImageUpload = ({
  imageUrl,
  onUploadFile,
  onClearImage,
  attachments,
  onSelectAttachmentUrl,
  accept = 'image/*',
  maxFiles = 1,
  dropzoneHeight = 195,
  loading,
  aspectRatio = 2 / 1,
  sourceImageUrl = null,
  initialCrop = null
}) => {
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [fileNameToCrop, setFileNameToCrop] = useState('');
  // The untouched file the user picked, kept so it can be stored alongside the crop.
  const [sourceFile, setSourceFile] = useState(null);
  // Whether the cropper is showing the uncropped source. A crop box is only worth
  // storing when it is: measured against the already-cropped rendition it would be
  // in a different coordinate space and useless for deriving other ratios.
  const [croppingSource, setCroppingSource] = useState(false);
  const [cropToRestore, setCropToRestore] = useState(null);

  const clearCropState = () => {
    setImageToCrop(null);
    setFileNameToCrop('');
    setSourceFile(null);
    setCroppingSource(false);
    setCropToRestore(null);
  };

  // A freshly picked file IS its own uncropped source, so the box we are about to
  // measure belongs with it. Oversized picks keep today's behaviour instead: crop
  // uploaded, no source, no box.
  const adoptSourceFile = (file) => {
    // Strictly less than, matching validates_attachment_size's less_than: a file of
    // exactly MAX_SOURCE_BYTES would pass here and then fail the save, taking the
    // crop the user just made with it.
    const withinSourceLimit = file.size < MAX_SOURCE_BYTES;
    setSourceFile(withinSourceLimit ? file : null);
    setCroppingSource(withinSourceLimit);
    setCropToRestore(null);
  };

  const handleFileDrop = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];

    try {
      const dataUrl = await fileToDataUrl(file);
      adoptSourceFile(file);
      setImageToCrop(dataUrl);
      setFileNameToCrop(file.name);
      setCropModalOpen(true);
    } catch {
      toastError('Failed to read image file. Please try again.');
    }
  };

  const handleCropComplete = (croppedFile, crop) => {
    onUploadFile && onUploadFile(croppedFile, sourceFile, croppingSource ? crop : null);
    clearCropState();
  };

  const handleCropModalClose = () => {
    setCropModalOpen(false);
    clearCropState();
  };

  const handleEditImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imageUrl) return;

    // Reframe the pre-crop source when we kept one. Re-cropping the displayed
    // rendition compounds a crop-and-downscale every time it is edited.
    const editingSource = !!sourceImageUrl;

    setSourceFile(null);
    setCroppingSource(editingSource);
    setCropToRestore(editingSource ? initialCrop : null);
    setImageToCrop(editingSource ? sourceImageUrl : imageUrl);
    setFileNameToCrop('cropped-event-image.jpg');
    setCropModalOpen(true);
  };

  if (loading) {
    return (
      <div className="mb-4 position-relative">
        <div className="">
          <div style={{
            width: '100%',
            height: `${dropzoneHeight}px`,
            border: '2px dashed var(--sl-border-primary)'
          }}>
          </div>
        </div>
        <img className="img-fluid position-absolute absolute-position-center"
             src="/uploading-loading.gif"
             style={{width: '25px'}} />
      </div>
    );
  }

  if (imageUrl) {
    return (
      <React.Fragment>
        <div className="mb-4 position-relative background-tint-on-hover-wrapper">
          <div className="background-tint-on-hover d-flex justify-content-center align-items-center"
               style={{ position: 'relative' }}>
            <img src={imageUrl}
                 style={{
                   maxHeight: '450px',
                   height: 'auto',
                   maxWidth: '100%',
                   width: 'auto'
                 }} />
            <div className="position-absolute d-flex" style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              gap: '16px',
              zIndex: 10
            }}>
              <a href="#"
                 onClick={handleEditImage}
                 title="Edit image"
                 style={{ cursor: 'pointer' }}>
                <i className="fas fa-pencil text-white"
                   style={{ fontSize: '20px' }}></i>
              </a>
              <a href="#"
                 onClick={(e) => { e.preventDefault(); onClearImage && onClearImage(); }}
                 title="Delete image"
                 style={{ cursor: 'pointer' }}>
                <i className="fas fa-trash text-white"
                   style={{ fontSize: '20px' }}></i>
              </a>
            </div>
          </div>
        </div>
        <ImageCropModal
          open={cropModalOpen}
          onClose={handleCropModalClose}
          onCropComplete={handleCropComplete}
          imageSrc={imageToCrop}
          initialCrop={cropToRestore}
          onImageReplaced={adoptSourceFile}
          aspectRatio={aspectRatio}
          fileName={fileNameToCrop}
        />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Dropzone maxFiles={maxFiles}
                accept={accept}
                onDrop={handleFileDrop}>
        {({getRootProps, getInputProps}) => (
          <section className="d-flex justify-content-center align-items-center text-center"
                   style={{
                     height: `${dropzoneHeight}px`,
                     border: '2px dashed var(--sl-border-primary)'
                   }}>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <i className="fas fa-cloud-upload-alt mb-2"
                 style={{
                   color: 'var(--sl-text-disabled)',
                   fontSize: '36px'
                 }}></i>
              <p className="mb-0">
                Drag-and-drop a file, or <a className="text-danger" onClick={(e) => e.preventDefault()} href="#">browse to upload</a>
              </p>
            </div>
          </section>
        )}
      </Dropzone>

      {attachments && attachments.length > 0 ? (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 rounded-0"
                 style={{background: 'var(--sl-bg-active)'}}>
              <div className="card-body p-3">
                <p className="mb-0">
                  <strong>
                    Choose from existing event assets
                  </strong>
                </p>
                {attachments.map((attachment, index) => (
                  <a key={index}
                     onClick={(e) => {
                       e.preventDefault();
                       onSelectAttachmentUrl && onSelectAttachmentUrl(attachment.resolved_file_url);
                     }}
                     href="#">
                    <div title={attachment.human_file_name}
                         className="d-inline-block mr-3 mt-3"
                         style={{
                           width: '55px',
                           height: '55px',
                           borderRadius: '2px',
                           backgroundSize: 'cover',
                           backgroundImage: `url(${attachment.compressed_file_url})`
                         }}>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ImageCropModal
        open={cropModalOpen}
        onClose={handleCropModalClose}
        onCropComplete={handleCropComplete}
        imageSrc={imageToCrop}
        initialCrop={cropToRestore}
        onImageReplaced={adoptSourceFile}
        aspectRatio={aspectRatio}
        fileName={fileNameToCrop}
      />
    </React.Fragment>
  );
};

DetailImageUpload.propTypes = {
  imageUrl: PropTypes.string,
  onUploadFile: PropTypes.func,
  onClearImage: PropTypes.func,
  attachments: PropTypes.array,
  onSelectAttachmentUrl: PropTypes.func,
  accept: PropTypes.string,
  maxFiles: PropTypes.number,
  dropzoneHeight: PropTypes.number,
  loading: PropTypes.bool,
  aspectRatio: PropTypes.number,
  sourceImageUrl: PropTypes.string,
  initialCrop: PropTypes.shape({
    left: PropTypes.number.isRequired,
    top: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  })
};

export default DetailImageUpload;


